import { Checkbox, FontIcon, Icon, IIconProps, ILabelStyles, ILinkStyles, ISearchBoxStyles, IStackItemStyles, IStackTokens, Label, Link, mergeStyles, SearchBox, Slider, Stack, StackItem, TooltipHost } from "@fluentui/react";
import { getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import dayjs from "dayjs";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { IOneDriveRootDetails } from "../../../src/taskpane/interfaces/IOneDriveRootDetails";
import { IPageSettings } from "../../../src/taskpane/interfaces/IPageSettings";
import { FilterItem, FilterSection } from "../../../src/taskpane/redux/models/reduxModels";
import { setContentManagerFilterSectionItem, setContentManagerFilterSectionItems, setContentManagerFilterSectionItemsFromRange, setContentManagerFilterSectionToggleShowMOre, setContentManagerFilterSectionToggleSlider } from "../../../src/taskpane/redux/modules/contentManagerDirectory";
import { toggleReload } from "../../../src/taskpane/redux/modules/reload";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { logger } from "../../../utilities/logger";
import { CancellableSharePointRestApi } from "../../../src/taskpane/services/CancellableSharePointRestApi";
import { RootState } from "../../../src/taskpane/components/_v2/redux/storeMain";
import { DatasourceHelper } from "../../../src/taskpane/components/helpers/dataSourceHelper";
import { Filter20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/Filter20Regular";
import { FilterDismiss20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/FilterDismiss20Regular";

export interface DateRangeStep {
  index: number;
  label: string;
  daysOffset?: number;
}

const checkboxLabelStyles: IStackItemStyles = {
  root: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap"
  }
}

const labelStyles: ILabelStyles = {
  root: { marginBottom: 10 }
}

const linkStyles: ILinkStyles = {
  root: { marginTop: 10 }
}

const filterButtonStackItemStyles: IStackItemStyles = {
  root: {
    marginTop: -10
  }
}

const searchIcon: IIconProps = { iconName: "Search" };
const searchBoxStyles: Partial<ISearchBoxStyles> = {
  root: { height: "100%", marginBottom: 10, paddingBottom: 5 }
};

const horizontalGapStackTokens: IStackTokens = {
  childrenGap: 10
};

export interface CMFileFilterPanelSectionProps {
  filterSections: FilterSection[];
  filterSection: FilterSection;
  isInOneDrive: boolean;
  serverRelPath: string;
  oneDriveRootDetails: IOneDriveRootDetails;
  currentDirectory: IPageSettings;
  isInContentManagerMode: boolean;
  setFilterSectionItem?: (filterSection: FilterSection, sectionItem: FilterItem, isChecked: boolean) => void;
  setFilterSectionItems?: (filterSection: FilterSection, sectionItems: FilterItem[]) => void;
  setFilterSectionItemsFromRange?: (filterSection: FilterSection, daysOffset: number) => void;
  setFilterSectionToggleSlider?: (filterSection: FilterSection, skipApiCall?: boolean) => void;
  setFilterSectionToggleShowMore?: (filterSection: FilterSection) => void;
}

export interface FilterSectionItemsExpandSearchParams {
  field: string;
  value: string;
}

export const CMFileFilterPanelSection = ({ filterSections, filterSection, isInOneDrive, serverRelPath, oneDriveRootDetails, currentDirectory, isInContentManagerMode, setFilterSectionItem, setFilterSectionItems, setFilterSectionItemsFromRange, setFilterSectionToggleSlider, setFilterSectionToggleShowMore }: CMFileFilterPanelSectionProps) => {
  const dispatch = useDispatch();

  const languageStrings = React.useContext(LanguageContext);

  const datasourceItems = useSelector((state: RootState) => state.tenantInfo.datasources);
  const autoOpenFilterSearchbox = useSelector((state: RootState) => state.tenantInfo?.autoOpenFilterSearchbox);

  const [sectionElement, setSectionElement] = React.useState<JSX.Element>();
  const [minItems, setMinItems] = React.useState<number>(5);
  const [loadSection, setLoadSection] = React.useState(false);
  const [showFilterIcon, setShowFilterIcon] = React.useState(false);
  const [showFilterSearchBox, setFilterSearchBox] = React.useState(false);
  const [sectionFilterValue, setSectionFilterValue] = React.useState("");
  const [uniqueSearches, setUniqueSearches] = React.useState<string[]>([]);
  const [filterSearchValue, setFilterSearchValue] = React.useState<string>();

  const [dateRangeSteps, setDateRangeSteps] = React.useState<DateRangeStep[]>([
    { index: -5, label: languageStrings.FilterDateStepSince },
    { index: -4, label: languageStrings.FilterDateStepLast3Months, daysOffset: -92 },
    { index: -3, label: languageStrings.FilterDateStepLast30Days, daysOffset: -30 },
    { index: -2, label: languageStrings.FilterDateStepLast7Days, daysOffset: -7 },
    { index: -1, label: languageStrings.FilterDateStepSinceYesterday, daysOffset: -1 },
    { index: 0, label: languageStrings.FilterDateStepToday, daysOffset: 0 },
    { index: 1, label: languageStrings.FilterDateStepTomorrow, daysOffset: 1 },
    { index: 2, label: languageStrings.FilterDateStepIn7Days, daysOffset: 7 },
    { index: 3, label: languageStrings.FilterDateStepIn30Days, daysOffset: 30 },
    { index: 4, label: languageStrings.FilterDateStepIn3Months, daysOffset: 92 },
    { index: 5, label: languageStrings.FilterDateStepIn3MonthsOrMore, daysOffset: 93 }
  ]);

  const [selectedDateRangeStep, setSelectedDateRangeStep] = React.useState<DateRangeStep>(dateRangeSteps[0]);
  const [sliderValue, setSliderValue] = React.useState<number>(selectedDateRangeStep.index);
  const [hasFutureDateValues, setHasFutureDateValues] = React.useState<boolean>(false);

  const initializedAutoOpenSearchRef = React.useRef<string | null>(null);

  const isMultiSelectBoundField: boolean = React.useMemo(() => {
    try {
      if (filterSection?.filterField?.FieldType === "Text" && Array.isArray(datasourceItems)) {
        const datasourceField = DatasourceHelper.getInputDataSource(datasourceItems, filterSection.filterField.DisplayName, [currentDirectory?.siteDisplayName ?? ""], [currentDirectory?.DocumentLibrary ?? ""]);
        if (Array.isArray(datasourceField?.boundFields) && datasourceField.boundFields.length > 0) {
          const boundedField = datasourceField.boundFields.find(item => item.name?.split(",").some(name => name.trim().toLowerCase() === filterSection.filterField.DisplayName.trim().toLowerCase()));
          if (boundedField.multiSelect) {
            return true;
          }
        }
      }
    } catch (error) {
      // ignore
    }
    return false;
  }, [datasourceItems, filterSection]);

  const onChange = (value: number) => {
    if (value !== selectedDateRangeStep.index) {
      const dateRangeStep = dateRangeSteps.find(x => x.index === value);
      setSelectedDateRangeStep(dateRangeStep);
      setSliderValue(value);
    }
  };

  const onChanged = (event: any, value: number, range?: [number, number]) => {
    if (selectedDateRangeStep.index !== value) {
      const dateRangeStep = dateRangeSteps.find(x => x.index === value);
      if (isInContentManagerMode) {
        dispatch(setContentManagerFilterSectionItemsFromRange(filterSection, dateRangeStep.daysOffset));
        dispatch(toggleReload());
      }
      else {
        setFilterSectionItemsFromRange(filterSection, dateRangeStep.daysOffset);
      }
    }
  };

  React.useEffect(() => {
    let debounceTimer: NodeJS.Timeout;
    if (sectionFilterValue?.length > 1) {
      debounceTimer = setTimeout(() => {
        const searchValue = sectionFilterValue.toLowerCase();
        const minItemsBeforeAdditionalSearch = 6;
        if (filterSection.items?.length > minItemsBeforeAdditionalSearch && sectionFilterValue.length > 0 && !uniqueSearches.includes(searchValue)) {
          setFilterSearchValue(sectionFilterValue);
        }
      }, 150);
    }

    const currentFilterSection = filterSections.find(x => x.filterField.RealFieldName === filterSection.filterField.RealFieldName)
    if (currentFilterSection) {
      if (currentFilterSection.filterField.Type === 'DateTime') {
        const selectedDateRange = (!currentFilterSection.daysOffset && currentFilterSection.daysOffset !== 0) ? dateRangeSteps[0] : dateRangeSteps.find(x => x.daysOffset === currentFilterSection.daysOffset);
        setSelectedDateRangeStep(selectedDateRange);
        setSliderValue(selectedDateRange.index);

        let hasFutureDates: boolean = false;
        if (currentFilterSection.items.length > 0) {
          try {
            const filterDate = new Date(currentFilterSection.items[currentFilterSection.items.length - 1].value);
            const filterTimeStamp = filterDate.getTime();
            if (!isNaN(filterTimeStamp) && filterTimeStamp > (new Date()).getTime()) {
              hasFutureDates = true;
              setHasFutureDateValues(true);
            }
          } catch (error) {
            // Ignore
          }
        }

        if (!hasFutureDates) {
          setDateRangeSteps(dateRangeSteps.slice(0, 6));
        }
      }

      const itemHeight: number = 30;
      let containerHeight: number;

      if (currentFilterSection.items.length > minItems && currentFilterSection.showMoreExpanded === false) {
        containerHeight = itemHeight * minItems;
      }
      else {
        containerHeight = itemHeight * currentFilterSection.items.length;
      }

      const shouldShowFilterTools = !currentFilterSection.showSlider && currentFilterSection.items.length > minItems;
      if (showFilterIcon !== shouldShowFilterTools) {
        setShowFilterIcon(shouldShowFilterTools);
      }

      if (shouldShowFilterTools && autoOpenFilterSearchbox && initializedAutoOpenSearchRef.current !== currentFilterSection.filterField.RealFieldName) {
        setFilterSearchBox(true);
        initializedAutoOpenSearchRef.current = currentFilterSection.filterField.RealFieldName;
      }

      const sectionStyle = mergeStyles({
        maxHeight: containerHeight - 10,
        overflow: "hidden",
        transition: "max-height 0.3s ease-in-out",
      });

      if (currentFilterSection?.items?.length > 0) {
        setSectionElement(
          <Stack tokens={{ childrenGap: 10 }} className={sectionStyle}>
            {currentFilterSection.items
              .filter(x => x.title?.toLowerCase().includes(sectionFilterValue?.toLowerCase()))
              .slice(0, currentFilterSection.showMoreExpanded ? currentFilterSection.items.length : currentFilterSection.items.length > minItems ? minItems : currentFilterSection.items.length)
              .map((item, index) => {
                return (
                  <Checkbox key={`${item.title}-${index}`} styles={{ root: { lineHeight: 40 } }} onRenderLabel={() => _onRenderLabel(item)} checked={item.selected} onChange={(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, isChecked?: boolean) => _onCheckboxChange(ev, isChecked, item)} />
                )
              })}
          </Stack>
        );
      } else {
        setSectionElement(undefined);
      }

      setLoadSection(true);
    }

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [filterSection, sectionFilterValue, autoOpenFilterSearchbox]);

  React.useEffect(() => {
    let cancellableSharePointRestApiService: CancellableSharePointRestApi;

    if (filterSearchValue?.length > 0) {
      (async () => {
        try {
          cancellableSharePointRestApiService = new CancellableSharePointRestApi(currentDirectory?.webUrl);

          const items: string[] = await cancellableSharePointRestApiService.getSharepointFilterValues(
            isInOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
            currentDirectory?.sharePointListId,
            serverRelPath,
            filterSection.filterField.RealFieldName,
            filterSearchValue
          );

          setUniqueSearches(prevSearches => [...prevSearches, filterSearchValue.toLowerCase()]);

          const oldSectionItems = [...filterSection.items];
          const newSectionItems: FilterItem[] = [...filterSection.items];

          const existingValues = new Set(filterSection.items.map(item => item.value?.toUpperCase()));

          items.filter(Boolean).forEach(item => {
            const filterValues: string[] = isMultiSelectBoundField
              ? item.split(",").map(v => v.trim()).filter(Boolean)
              : [item];

            filterValues.forEach(filterValue => {
              const upperCaseFilterValue = filterValue.toUpperCase();

              if (!existingValues.has(upperCaseFilterValue)) {
                existingValues.add(upperCaseFilterValue);

                newSectionItems.push({
                  title: filterValue,
                  value: filterValue,
                  selected: false
                });
              }
            });
          });

          if (newSectionItems?.length > oldSectionItems?.length) {
            const isBoekjaar = filterSection.filterField.FieldType === "Number" &&
              (filterSection.filterField.RealFieldName === "Boekjaar" || filterSection.filterField.DisplayName?.toLowerCase() === "boekjaar");

            const sectionItemsSorted = newSectionItems
              .sort((a, b) => {
                if (isBoekjaar) {
                  const aNum = +a.value;
                  const bNum = +b.value;
                  if (isNaN(aNum) && isNaN(bNum)) return 0; // Both are bad, keep original order
                  if (isNaN(aNum)) return 1;                // A is bad, push it to the end
                  if (isNaN(bNum)) return -1;               // B is bad, push it to the end
                  // Both are good, sort in descending order
                  return bNum - aNum;
                }

                return a.title?.toLowerCase() > b.title?.toLowerCase() ? 1 : -1;
              })
              .map((item, index) => {
                return {
                  ...item,
                  index
                }
              });

            if (isInContentManagerMode)
              dispatch(setContentManagerFilterSectionItems(filterSection, sectionItemsSorted));
            else
              setFilterSectionItems(filterSection, sectionItemsSorted);
          }
        } catch (error) {
          logger(error, "CMFileFilterPanelSection.tsx (Search filters)");
        }
      })();
    }

    return () => {
      cancellableSharePointRestApiService?.cancel(currentDirectory?.webUrl);
    };
  }, [filterSearchValue]);

  const _onRenderLabel = (item: FilterItem) => {
    return (
      <div style={{ paddingLeft: '5px', lineHeight: '20px' }} >
        <Stack horizontal tokens={{ childrenGap: 5 }} verticalAlign="center">
          {filterSection.filterField.RealFieldName === 'DocIcon' && <Icon {...getFileTypeIconProps({ extension: item.fileType, size: 16, imageFileType: "png" })} />}
          <Stack.Item styles={checkboxLabelStyles}>{item.title}</Stack.Item>
        </Stack>
      </div>
    )
  }

  function _onCheckboxChange(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, isChecked?: boolean, item?: FilterItem) {

    if (isInContentManagerMode) {
      dispatch(setContentManagerFilterSectionItem(filterSection, item, isChecked));
      dispatch(toggleReload());
    } else {
      setFilterSectionItem(filterSection, item, isChecked);
    }
  }

  const showMoreClicked = () => {
    if (isInContentManagerMode) {
      dispatch(setContentManagerFilterSectionToggleShowMOre(filterSection))
    } else {
      setFilterSectionToggleShowMore(filterSection);
    }
  }

  const showDatesClicked = () => {
    const reloadGrid: boolean = selectedDateRangeStep.index !== dateRangeSteps[0].index;
    setSelectedDateRangeStep(dateRangeSteps[0]);
    setSliderValue(0);
    if (isInContentManagerMode) {
      dispatch(setContentManagerFilterSectionItemsFromRange(filterSection, dateRangeSteps[0].daysOffset));
      dispatch(setContentManagerFilterSectionToggleSlider(filterSection));
      if (reloadGrid)
        dispatch(toggleReload());
    }
    else {
      setFilterSectionItemsFromRange(filterSection, dateRangeSteps[0].daysOffset);
      setFilterSectionToggleSlider(filterSection, !reloadGrid);
    }
  }

  const showSliderClicked = () => {
    let reloadGrid = filterSection.items.some(x => x.selected);
    if (isInContentManagerMode) {
      dispatch(setContentManagerFilterSectionToggleSlider(filterSection));
      if (reloadGrid) {
        dispatch(toggleReload());
      }
    }
    else {
      setFilterSectionToggleSlider(filterSection, !reloadGrid);
    }
  }

  const getSectionSubtitle = () => {
    if (filterSection.showSlider) {
      if (selectedDateRangeStep.index === dateRangeSteps[0]?.index) {
        let hasEmptyItem = (filterSection.items.some(x => x.title === `(Empty)`) || filterSection.items.some(x => x.title === `(Leeg)`)) ? true : false;
        let firstItem = hasEmptyItem ? filterSection?.items[1] : filterSection?.items[0]
        let dateString = dayjs(firstItem?.value).isValid() ? dayjs(firstItem?.value).format("ll") : firstItem?.title
        return `(${selectedDateRangeStep.label} ${dateString})`;
      } else {
        return `(${selectedDateRangeStep.label})`;
      }
    }

    if (filterSection.items?.some(x => x.selected)) {
      return `(${filterSection.items?.filter(x => x.selected)?.length})`;
    }

    return '';
  }

  const onFilterClick = () => {
    setFilterSearchBox(prev => !prev);
    setSectionFilterValue("");
  }

  const onChangeFilter = React.useCallback(async (event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => {
    setSectionFilterValue(newValue);
  }, []);

  return (
    <>
      {loadSection && <>
        {filterSection?.items?.length > 0 &&
          <Stack horizontal tokens={horizontalGapStackTokens} verticalAlign="center" horizontalAlign="space-between">
            <StackItem>
              <Label styles={labelStyles}>{filterSection.filterField.DisplayName} {getSectionSubtitle()}</Label>
            </StackItem>
            {showFilterIcon &&
              <TooltipHost content={!showFilterSearchBox ? languageStrings.SearchSectionFilter.replace("{section}", filterSection.filterField.DisplayName) : languageStrings.ClearSearch}>
                <StackItem styles={filterButtonStackItemStyles}>
                  {/* <ActionButton iconProps={{ iconName: !showFilterSearchBox ? "Filter" : "ClearFilter" }} allowDisabledFocus onClick={onFilterClick} /> */}
                  {!showFilterSearchBox
                    ? <Filter20Regular style={{ cursor: "pointer" }} onClick={onFilterClick} />
                    : <FilterDismiss20Regular style={{ cursor: "pointer" }} onClick={onFilterClick} />}
                </StackItem>
              </TooltipHost>}
          </Stack>
        }
        {showFilterIcon && showFilterSearchBox && <SearchBox
          styles={searchBoxStyles}
          placeholder={languageStrings.SearchSectionFilter.replace("{section}", filterSection.filterField.DisplayName)}
          iconProps={searchIcon}
          underlined={true}
          onChange={onChangeFilter}
          showIcon={true}
        />}
        {!filterSection.showSlider && sectionElement &&
          <>
            {sectionElement}
            {filterSection?.items?.filter(x => x.title?.toLowerCase().includes(sectionFilterValue?.toLowerCase())).length > minItems
              && <div className="xb-show-more-action" style={{ marginTop: 10, marginLeft: 4 }} onClick={() => { showMoreClicked() }}>
                <FontIcon iconName={!filterSection.showMoreExpanded ? "ChevronDown" : "ChevronUp"} />
                <span>{!filterSection.showMoreExpanded ? languageStrings.ShowMore : languageStrings.ShowLess}</span>
              </div>
            }
          </>}
        {filterSection.hasSlider && filterSection.showSlider && <Slider
          min={dateRangeSteps[0]?.index}
          max={dateRangeSteps[dateRangeSteps?.length - 1]?.index}
          value={sliderValue}
          showValue={false}
          snapToStep={false}
          onChange={onChange}
          onChanged={onChanged}
          originFromZero={hasFutureDateValues && sliderValue > dateRangeSteps[0]?.index}
        />}
        {filterSection.hasSlider && filterSection.showSlider && <Link styles={linkStyles} onClick={() => showDatesClicked()}>{languageStrings.SelectSpecificDates}</Link>}
        {filterSection.hasSlider && !filterSection.showSlider && <Link styles={linkStyles} onClick={() => showSliderClicked()}>{languageStrings.ShowDateRangeSlider}</Link>}
      </>}
    </>
  )
};
